/**
 * @module hooks/useHomeCheckout
 * @description
 *   Home page pricing/checkout state: pay-per-use vs. subscription toggle,
 *   promo code validation, Stripe Checkout redirect, and the Stripe billing
 *   portal ("Manage subscription") flow.
 *
 * @extracted-from client/src/App.js (lines ~56-66, 164-174, 733-821, 836-903, Phase 7)
 *
 * @invariants
 *   - POST /api/validate-coupon request/response shape unchanged
 *   - startStripeCheckout() params unchanged (paymentType, ppuQuantity, selectedSubTier, appliedCoupon, accountUser)
 *   - POST /api/create-billing-portal-session and POST /api/recover-subscription request/response shape unchanged
 *   - localStorage keys: criticai_subscription_id, criticai_subscription_customer_id
 *
 * @dependencies
 *   - api/client, api/authHeaders, apiConfig, billing/stripeCheckout, constants/pricing, utils/analytics, react-hot-toast
 */

import { useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { toast } from 'react-hot-toast';
import api from '../api/client';
import { apiFetch } from '../api/authHeaders';
import { API_BASE_URL } from '../apiConfig';
import { startStripeCheckout } from '../billing/stripeCheckout';
import { PRICING_TIERS, SUBSCRIPTION_PLANS } from '../constants/pricing';
import analytics from '../utils/analytics';

/**
 * @param {Object} params
 * @param {{ email?: string, id?: string }|null} params.billingAccountUser — server session user (or Firebase fallback), used for subscription checkout.
 * @param {Object|null} params.user — signed-in Firebase user; gates the billing-portal flow (requires an Express session).
 * @param {import('react').RefObject<HTMLElement>} params.pricingSectionRef — ref to the "Plans & credits" section, used as a scroll fallback.
 * @param {boolean} [params.hasActiveSubscription=false] — drives the returned `showManageSubscription` flag.
 * @returns {Object} everything `PricingSection` and the header's "Manage subscription" shortcut need.
 */
export function useHomeCheckout({ billingAccountUser, user, pricingSectionRef, hasActiveSubscription = false }) {
  const [homePaymentType, setHomePaymentType] = useState('one-time');
  const [homePpuQuantity, setHomePpuQuantity] = useState(1);
  const [homeSelectedSubTier, setHomeSelectedSubTier] = useState('200');
  const [homeCouponCode, setHomeCouponCode] = useState('');
  const [homeCouponError, setHomeCouponError] = useState('');
  const [homeAppliedCoupon, setHomeAppliedCoupon] = useState(null);
  const [homeCheckoutLoading, setHomeCheckoutLoading] = useState(false);
  const [homeCouponLoading, setHomeCouponLoading] = useState(false);

  // Optional: pre-fill promo from ?coupon= in the URL
  useEffect(() => {
    try {
      const param = new URLSearchParams(window.location.search).get('coupon');
      if (param && param.trim()) {
        setHomeCouponCode(param.trim().toUpperCase());
      }
    } catch {
      /* ignore */
    }
  }, []);

  const validateHomeCoupon = async () => {
    if (!homeCouponCode.trim()) {
      setHomeCouponError('Please enter a coupon code');
      return;
    }
    setHomeCouponLoading(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/validate-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          couponCode: homeCouponCode.trim().toUpperCase(),
        }),
      });
      const result = await response.json();
      if (response.ok) {
        setHomeAppliedCoupon(result.coupon);
        setHomeCouponError('');
        toast.success(
          `Coupon applied! ${result.coupon.percent_off ? `${result.coupon.percent_off}% off` : `$${result.coupon.amount_off / 100} off`}`
        );
      } else {
        setHomeCouponError(result.error || 'Invalid coupon code');
        setHomeAppliedCoupon(null);
        toast.error(result.error || 'Invalid coupon code');
      }
    } catch {
      setHomeCouponError('Failed to validate coupon');
      setHomeAppliedCoupon(null);
      toast.error('Failed to validate coupon');
    } finally {
      setHomeCouponLoading(false);
    }
  };

  const removeHomeCoupon = () => {
    setHomeAppliedCoupon(null);
    setHomeCouponCode('');
    setHomeCouponError('');
  };

  const handleHomeStripeCheckout = async () => {
    setHomeCheckoutLoading(true);
    try {
      analytics.trackButtonClick(
        'checkout_from_home',
        `${homePaymentType}|tier:${homeSelectedSubTier}|qty:${homePpuQuantity}|coupon:${homeAppliedCoupon?.id || 'none'}`
      );
      await startStripeCheckout({
        paymentType: homePaymentType === 'subscription' ? 'subscription' : 'one-time',
        selectedTier: 'pay_per_use',
        ppuQuantity: homePpuQuantity,
        selectedSubTier: homeSelectedSubTier,
        appliedCoupon: homeAppliedCoupon,
        accountUser: billingAccountUser,
      });
    } catch (error) {
      console.error('Checkout error:', error);
      let errorMessage = error.message || 'Payment failed. Please try again.';
      if (
        errorMessage === 'Failed to fetch' ||
        (error && error.name === 'TypeError' && /fetch/i.test(String(errorMessage)))
      ) {
        errorMessage =
          'Could not reach the payment server. Check your connection. If you run the app locally, set REACT_APP_API_BASE_URL in client/.env or start the API on port 3001.';
      }
      toast.error(errorMessage.length > 120 ? 'Could not reach the payment server. Please try again.' : errorMessage);
    } finally {
      setHomeCheckoutLoading(false);
    }
  };

  /** Header / upgrade — show pay-per-use tab and scroll to the credits block. */
  const scrollToPurchaseCreditsSection = useCallback(() => {
    flushSync(() => {
      setHomePaymentType('one-time');
    });
    const creditsBlock = document.getElementById('purchase-credits');
    if (creditsBlock) {
      creditsBlock.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      pricingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    toast('Choose credits below — checkout is on Stripe.');
    analytics.trackButtonClick('purchase_analyses', 'header');
  }, [pricingSectionRef]);

  const homeSelectedTierData = PRICING_TIERS[0];
  const homeOneTimeTotal = homePpuQuantity * 1.0;
  const homeSubPlan =
    SUBSCRIPTION_PLANS.find((p) => p.id === homeSelectedSubTier) || SUBSCRIPTION_PLANS[0];

  const showManageSubscription = hasActiveSubscription;

  const handleManageSubscription = async () => {
    // Portal requires a signed-in Express session — prompt Google auth on mobile when missing
    if (!user) {
      toast('Sign in with Google to manage your subscription');
      document.getElementById('criticai-google-signin')?.click();
      return;
    }

    try {
      const returnUrl = window.location.origin + window.location.pathname;

      // Get customer ID from localStorage if available (for recovery)
      const storedCustomerId = localStorage.getItem('criticai_subscription_customer_id');

      const response = await apiFetch(`${API_BASE_URL}/api/create-billing-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          returnUrl: returnUrl,
          customerId: storedCustomerId || undefined,
        }),
      });

      const data = await response.json();

      if (response.ok && data.url) {
        // Redirect to Stripe billing portal
        window.location.href = data.url;
      } else {
        const errorMessage = data.error || 'Failed to open subscription management';
        toast.error(errorMessage);

        // If no subscription found, try to recover subscription first
        if (data.code === 'NO_SUBSCRIPTION' || data.code === 'NO_ACTIVE_SUBSCRIPTION') {
          const storedSubscriptionId = localStorage.getItem('criticai_subscription_id');
          if (storedSubscriptionId || storedCustomerId) {
            try {
              const recoveryResponse = await api.post('/api/recover-subscription', {
                subscriptionId: storedSubscriptionId,
                customerId: storedCustomerId,
              });

              if (recoveryResponse.data.subscription && recoveryResponse.data.subscription.status === 'active') {
                // Subscription recovered, try again
                toast.success('Subscription recovered! Opening management portal...');
                setTimeout(() => {
                  handleManageSubscription();
                }, 1000);
                return;
              }
            } catch (recoveryError) {
              console.error('Failed to recover subscription:', recoveryError);
            }
          }

          // If recovery failed or no stored data, refresh the page
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      toast.error('Failed to open subscription management. Please try again.');
    }
  };

  return {
    homePaymentType,
    setHomePaymentType,
    homePpuQuantity,
    setHomePpuQuantity,
    homeSelectedSubTier,
    setHomeSelectedSubTier,
    homeCouponCode,
    setHomeCouponCode,
    homeCouponError,
    setHomeCouponError,
    homeAppliedCoupon,
    setHomeAppliedCoupon,
    homeCheckoutLoading,
    homeCouponLoading,
    validateHomeCoupon,
    removeHomeCoupon,
    handleHomeStripeCheckout,
    scrollToPurchaseCreditsSection,
    homeSelectedTierData,
    homeOneTimeTotal,
    homeSubPlan,
    showManageSubscription,
    handleManageSubscription,
  };
}

export default useHomeCheckout;
